import {isSafeInteger} from "lodash"
import {
  BasketItem,
  DealProductSnapshot,
  BasketTotalWithBreakDownItems,
  BasketTotal,
  Deal,
  Product,
  BasketTotalWithBreakDown
} from "@irati/types/ops"
import discountPercentage from "./discountPercentage"

export interface TotalsData {
  deals: Deal[],
  products: Pick<Product, "uuid" | "vat">[]
}

export default function totals(basketItems: BasketItem[], data: TotalsData): BasketTotalWithBreakDown {
  const items = basketItems.map((item) => {
    const {product, productVAT} = getProductAndVAT(item, data)
    return {
      item,
      total: totalsForItem(item, product, productVAT)
    }
  })

  // round final values
  return {
    total: roundTotal(tallyTotals(items)),
    cashback: {amount: 0},
    items: roundItemsTotal(items)
  }
}

export function totalsForItem(item: BasketItem, product: DealProductSnapshot | undefined, productVAT: number): BasketTotal {
  const discount = product?.salePrice?.discount ? (1 - (product?.salePrice.discount / 100)) : 1
  const isOpenValue = isSafeInteger(item.openValue)
  // open value
  const isCentValue = !!isOpenValue
  let price = isOpenValue ? item.openValue! : (product?.salePrice?.value || 0)
  if (!isCentValue) {
    price = Math.round(price * 100)
  }
  const salePrice = price! * discount
  const currency = product?.salePrice?.currency!
  const count = item.count || 1

  const originalTotalPrice = price! * count
  const vat = Math.floor((salePrice / (1 + productVAT / 100)) * count * productVAT / 100!)
  const subtotal = salePrice * count

  return {
    discountPercentage: discountPercentage(originalTotalPrice, subtotal),
    discountPercentageAfterServiceCharge: discountPercentage(originalTotalPrice, subtotal),
    originalPrice: originalTotalPrice,
    amount: subtotal,
    serviceCharge: 0,
    currency,
    vat
  }
}

export function tallyTotals(breakdown: BasketTotalWithBreakDownItems[]): BasketTotal {
  const total = breakdown.reduce<BasketTotal>((runningTotal, current) => {
    return {
      discountPercentage: 0,
      originalPrice: runningTotal.originalPrice! + current!.total!.originalPrice!,
      currency: current.total?.currency,
      vat: runningTotal.vat! + current!.total!.vat!,
      amount: runningTotal.amount! + current!.total!.amount!,
      serviceCharge: runningTotal.serviceCharge! + current!.total!.serviceCharge!
    }
  }, {discountPercentage: 0, amount: 0, originalPrice: 0, currency: "GBP", vat: 0, serviceCharge: 0})
  total.discountPercentage = discountPercentage(total.originalPrice!, total.amount!)
  total.discountPercentageAfterServiceCharge = discountPercentage(total.originalPrice!, total.amount! + total.serviceCharge!)

  return total
}

export function getProductAndVAT(item: BasketItem, data: TotalsData) {
  const fullProduct = getFullProductForEntity(data, item)
  return {
    entity: data.deals.find((d) => d.uuid === item.entityId)!,
    product: fullProduct,
    productVAT: fullProduct.vat || 0
  }
}

function roundItemsTotal(items: BasketTotalWithBreakDownItems[]): BasketTotalWithBreakDownItems[] {
  return items.map(({total, ...rest}) => ({...rest, total: roundTotal(total!)}))
}

function roundTotal(item: BasketTotal): BasketTotal {
  item.vat = Math.round(item.vat!)
  item.originalPrice = Math.round(item.originalPrice!)
  item.amount = Math.round(item.amount!)

  return item
}

function getFullProductForEntity(data: TotalsData, item: BasketItem) {
  const dealProduct = data.deals.find((d) => d.uuid === item.entityId)?.productConfig?.products?.find((t) => t!.uuid === item.productId)!
  const product = data.products.find((t) => t.uuid === item.productId!)!
  return {...product, salePrice: dealProduct?.salePrice}
}
